import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Copy, Check, Mail } from "lucide-react";

// Brand marks aren't in lucide-react — small inline SVGs, colored to match each
// platform's actual brand mark rather than the neutral gray of the surrounding UI.
const XIcon = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-2.44 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppIcon = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.05 2C6.492 2 2 6.415 2 11.876c0 2.017.606 3.9 1.653 5.454L2 22l4.844-1.594c1.474.786 3.15 1.242 4.916 1.242h.005c5.558 0 10.05-4.415 10.05-9.877C21.816 6.415 17.324 2 11.766 2h.284zm.005 18.045h-.004a8.24 8.24 0 0 1-4.196-1.15l-.301-.179-3.117 1.026 1.04-3.037-.198-.312a8.148 8.148 0 0 1-1.28-4.417c0-4.532 3.688-8.219 8.223-8.219 2.198 0 4.264.856 5.816 2.406a8.13 8.13 0 0 1 2.408 5.816c0 4.532-3.688 8.066-8.191 8.066z" />
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.149-.15.297-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.05-.52-.099-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.05 3.133 4.966 4.27 2.916 1.137 2.916.759 3.443.712.526-.05 1.758-.712 2.005-1.4.248-.687.248-1.276.173-1.4-.074-.124-.272-.198-.57-.347z" />
  </svg>
);

interface ShareLinkButtonsProps {
  shareUrl: string;
  shareMessage: string;
  copyToastDescription?: string;
}

/** Copy-link input plus X/WhatsApp share buttons — shared between the invite and influencer programs. */
export const ShareLinkButtons = ({
  shareUrl,
  shareMessage,
  copyToastDescription = "Link copied to clipboard.",
}: ShareLinkButtonsProps) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({ title: "Copied!", description: copyToastDescription });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ variant: "destructive", title: "Couldn't copy", description: "Please copy the link manually." });
    }
  };

  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}&url=${encodeURIComponent(shareUrl)}`;
  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(`${shareMessage} ${shareUrl}`)}`;
  const emailShareUrl = `mailto:?subject=${encodeURIComponent("Join me on Joylo")}&body=${encodeURIComponent(`${shareMessage} ${shareUrl}`)}`;

  return (
    <>
      <div className="flex items-center gap-2 mb-5">
        <input
          readOnly
          aria-label="Your share link"
          value={shareUrl}
          className="min-h-[42px] flex-1 truncate rounded-[9px] border border-input bg-surface px-3.5 text-[14px] text-foreground"
          onFocus={(e) => e.currentTarget.select()}
        />
        <button
          onClick={handleCopy}
          className="flex min-h-[42px] shrink-0 items-center gap-2 rounded-[9px] border border-input bg-surface px-3.5 text-[13px] font-semibold text-foreground transition-colors hover:bg-soft"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground">Share via</span>
        <a
          href={twitterShareUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on X"
          className="p-2 rounded-full border border-line text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-smooth"
        >
          <XIcon className="h-4 w-4" />
        </a>
        <a
          href={whatsappShareUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on WhatsApp"
          className="p-2 rounded-full border border-line text-[#25D366] hover:bg-[#25D366]/10 transition-smooth"
        >
          <WhatsAppIcon className="h-4 w-4" />
        </a>
        <a
          href={emailShareUrl}
          aria-label="Share via Email"
          className="p-2 rounded-full border border-line text-muted-foreground hover:bg-soft transition-smooth"
        >
          <Mail className="h-4 w-4" />
        </a>
      </div>
    </>
  );
};
